/**
 * useSEO — dynamic per-page SEO (title, meta, canonical, JSON-LD)
 * Usage: useSEO({ title, description, canonical, jsonLd })
 */

const SITE_NAME = 'ExpertAtDoor';
const BASE_URL  = 'https://www.expertatdoor.com';
const BASE_DESC = 'Book trusted home services in Kolkata — cleaning, laptop repair, printer repair, electrician, car wash & more. Pay only after service.';

export const useSEO = ({
  title,
  description,
  canonical,
  image,
  jsonLd,
  noindex = false,
} = {}) => {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} — Home Services in Kolkata`;
  const metaDesc  = description || BASE_DESC;
  const ogImage   = image || `${BASE_URL}/ead.jpeg`;
  const canonURL  = canonical ? `${BASE_URL}${canonical}` : BASE_URL;

  // Title
  document.title = fullTitle;

  // Helper: upsert <meta> by selector
  const setMeta = (selector, value, attr = 'content') => {
    let el = document.querySelector(selector);
    if (!el) {
      el = document.createElement('meta');
      const [attrName, attrVal] = selector.replace(/[\[\]]/g, '').split('=').map(s => s.replace(/"/g, ''));
      el.setAttribute(attrName, attrVal);
      document.head.appendChild(el);
    }
    el.setAttribute(attr, value);
  };

  // Helper: upsert <link rel="...">
  const setLink = (rel, href) => {
    let el = document.querySelector(`link[rel="${rel}"]`);
    if (!el) {
      el = document.createElement('link');
      el.setAttribute('rel', rel);
      document.head.appendChild(el);
    }
    el.setAttribute('href', href);
  };

  // Helper: upsert JSON-LD script by id
  const setJsonLd = (id, data) => {
    let el = document.getElementById(id);
    if (!el) {
      el = document.createElement('script');
      el.setAttribute('type', 'application/ld+json');
      el.setAttribute('id', id);
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(data);
  };

  // ── Standard meta ───────────────────────────────────────
  setMeta('meta[name="description"]', metaDesc);
  setMeta('meta[name="robots"]', noindex ? 'noindex,nofollow' : 'index,follow');
  setLink('canonical', canonURL);

  // ── Open Graph ──────────────────────────────────────────
  setMeta('meta[property="og:title"]',       fullTitle);
  setMeta('meta[property="og:description"]', metaDesc);
  setMeta('meta[property="og:url"]',         canonURL);
  setMeta('meta[property="og:image"]',       ogImage);

  // ── Twitter ─────────────────────────────────────────────
  setMeta('meta[name="twitter:title"]',       fullTitle);
  setMeta('meta[name="twitter:description"]', metaDesc);
  setMeta('meta[name="twitter:image"]',       ogImage);

  // ── Custom JSON-LD ──────────────────────────────────────
  if (jsonLd) {
    const id = 'ld-page-schema';
    setJsonLd(id, jsonLd);
  }
};

export default useSEO;
